/**
 * Atlas Advisor suggested questions API client and react-query hooks.
 *
 * Two separate endpoints controlled by feature flags:
 *   /hardcoded   — no auth, template DDB (atlas_advisor_nav flag)
 *   /personalized — JWT auth, personalized DDB (atlas_advisor_llm_questions flag)
 *
 * useAdvisorSuggestedQuestions() is the composite hook consumed by
 * SearchInputBar — it tries personalized first (when the LLM flag is on),
 * then falls back to hardcoded. Both read from the advisor_suggested_questions table.
 */

import { useQuery } from '@tanstack/react-query';
import { getAppName, shouldUseAtlasLLMQuestions, shouldShowBeaconAdvisor } from 'src/utils/app';
import { AppName } from 'sl-api-connector/types';
import { getAdvisorServiceUrl, getAuthHeader } from './advisorContext';

const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------------------------------------
// Fetch functions
// ---------------------------------------------------------------------------

export async function fetchHardcodedQuestions(
  entityType: string,
  tab: string,
  subTab: string
): Promise<string[]> {
  const baseUrl = getAdvisorServiceUrl();
  const appName = getAppName()?.toLowerCase() || 'atlas';
  const params = new URLSearchParams({ entityType, tab, subTab, appName });
  const url = `${baseUrl}/api/v3/suggested-questions/hardcoded?${params.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    console.error(`Hardcoded questions fetch failed: ${response.status}`);
    return [];
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchPersonalizedQuestions(
  entityType: string,
  tab: string,
  subTab: string,
  userEmail: string
): Promise<string[]> {
  const baseUrl = getAdvisorServiceUrl();
  const appName = getAppName()?.toLowerCase() || 'atlas';
  const params = new URLSearchParams({ entityType, tab, subTab, userEmail, appName });
  const url = `${baseUrl}/api/v3/suggested-questions/personalized?${params.toString()}`;

  const headers = await getAuthHeader();
  const response = await fetch(url, { headers });

  if (!response.ok) {
    console.error(`Personalized questions fetch failed: ${response.status}`);
    return [];
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

// ---------------------------------------------------------------------------
// Individual hooks
// ---------------------------------------------------------------------------

export function useHardcodedQuestions(
  entityType: string | undefined,
  tab: string | undefined,
  subTab: string | undefined
) {
  const appName = getAppName();
  const isAdvisorApp = appName === AppName.Atlas || (appName === AppName.Beacon && shouldShowBeaconAdvisor());
  const enabled = Boolean(entityType && tab && subTab) && isAdvisorApp;

  return useQuery({
    queryKey: ['advisorHardcodedQuestions', entityType, tab, subTab],
    queryFn: () => fetchHardcodedQuestions(entityType!, tab!, subTab!),
    enabled,
    staleTime: STALE_TIME_MS
  });
}

export function usePersonalizedQuestions(
  entityType: string | undefined,
  tab: string | undefined,
  subTab: string | undefined,
  userEmail: string | undefined
) {
  const enabled =
    Boolean(entityType && tab && subTab && userEmail) &&
    getAppName() === AppName.Atlas &&
    shouldUseAtlasLLMQuestions();

  return useQuery({
    queryKey: ['advisorPersonalizedQuestions', entityType, tab, subTab, userEmail],
    queryFn: () => fetchPersonalizedQuestions(entityType!, tab!, subTab!, userEmail!),
    enabled,
    staleTime: STALE_TIME_MS
  });
}

// ---------------------------------------------------------------------------
// Composite hook (drop-in replacement — SearchInputBar stays unchanged)
// ---------------------------------------------------------------------------

export function useAdvisorSuggestedQuestions(
  entityType: string | undefined,
  tab: string | undefined,
  subTab: string | undefined,
  userEmail: string | undefined
) {
  const personalized = usePersonalizedQuestions(entityType, tab, subTab, userEmail);
  const hardcoded = useHardcodedQuestions(entityType, tab, subTab);

  const hasPersonalized =
    personalized.isSuccess && Array.isArray(personalized.data) && personalized.data.length > 0;

  const data = hasPersonalized ? personalized.data : hardcoded.data;
  const isLoading = personalized.isLoading || (!hasPersonalized && hardcoded.isLoading);
  const isError = personalized.isError && hardcoded.isError;

  return { data, isLoading, isError };
}
